import { ru } from './ru'

interface AppError {
  code: string
  message: string
  context?: {
    resource?: string
    limit?: number
    tier?: string
    [key: string]: any
  }
}

// Функция для определения правильной формы слова по числу
function pluralize(count: number, forms: { one: string; few: string; many: string }): string {
  const n = Math.abs(count)
  const n10 = n % 10
  const n100 = n % 100

  if (n10 === 1 && n100 !== 11) {
    return forms.one
  }
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) {
    return forms.few
  }
  return forms.many
}

// Функция для замены плейсхолдеров в строке
function interpolate(template: string, context: Record<string, any>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return context[key]?.toString() || match
  })
}

/**
 * Переводит ошибку с бэкенда на русский язык
 * @param error - Ошибка от бэкенда (может быть string или AppError)
 * @returns Локализованное сообщение об ошибке
 */
export function translateError(error: string | AppError | any): string {
  // Если это просто строка, возвращаем как есть
  if (typeof error === 'string') {
    return error
  }

  // Если это объект с кодом ошибки
  if (error && typeof error === 'object') {
    const appError = error as AppError
    const code = appError.code

    // Если код неизвестен, возвращаем fallback
    if (!code || !ru.errors[code as keyof typeof ru.errors]) {
      return appError.message || ru.errors.UNKNOWN
    }

    const template = ru.errors[code as keyof typeof ru.errors]

    // Специальная обработка для ERR_LIMIT_EXCEEDED
    if (code === 'ERR_LIMIT_EXCEEDED' && appError.context) {
      const { resource, limit, tier } = appError.context

      // Переводим название тарифа
      const tierName = tier ? ru.tiers[tier as keyof typeof ru.tiers] || tier : ''

      // Определяем правильную форму ресурса
      let resourceName = resource
      if (resource && ru.resources[resource as keyof typeof ru.resources]) {
        const forms = ru.resources[resource as keyof typeof ru.resources]
        resourceName = `${limit} ${pluralize(limit!, forms)}`
      }

      return interpolate(template, {
        tier: tierName,
        limit,
        resource: resourceName,
      })
    }

    // Специальная обработка для ERR_SOFT_LOCKED
    if (code === 'ERR_SOFT_LOCKED' && appError.context) {
      // Return the localized message with exceeded resource details
      const resourceDetails = Object.entries(appError.context)
        .map(([key, val]: [string, any]) => {
          const label = ru.gracePeriod?.resourceLabels?.[key as keyof typeof ru.gracePeriod.resourceLabels] || key
          return `${label}: ${val.current}/${val.limit}`
        })
        .join(', ')
      return `${template} (${resourceDetails})`
    }

    // Обычная интерполяция для других ошибок
    if (appError.context) {
      return interpolate(template, appError.context)
    }

    return template
  }

  // Fallback
  return ru.errors.UNKNOWN
}
